import { useEffect, useRef, useState } from 'react';

import { EntityLink } from '@/components/nav/EntityLink';
import { Badge } from '@/components/ui/badge';
import { NotePopover } from '@/components/ui/note-popover';
import { TableWrap } from '@/components/ui/table';
import type { Project, TimeLog } from '@/lib/api';
import { hoursNum } from '@/lib/format';
import { type NavTarget, toMember, toProject } from '@/lib/nav';
import {
  buildGrid,
  type GridAxis,
  type GridDay,
  type GridRow,
  MAX_DAILY_HOURS,
  monthDays,
} from '@/lib/timelogs';
import { cn } from '@/lib/utils';

import { CellNotes } from './CellNotes';
import { TimeLogEditor } from './TimeLogEditor';

/**
 * 月次の工数グリッド。
 *
 * 縦は「メンバー × プロジェクト」、横はその月の日。
 * 締めのときに見たいのは「誰のどのプロジェクトが、どの日に何時間か」なので、
 * 記録を時系列に積むのではなくこの形に畳む。
 *
 * マスを押すとその行の直下が開いて、その日の記録を 1 件ずつ直せる。
 * 空のマスを押せば同じ場所がそのまま追加フォームになる。
 * ただし追加できるのは自分の行だけなので、他人の行の空きマスは押せない。
 *
 * メモは MCP 側の AI が書くので改行入りで長い。マスには印だけを出し、
 * 全文はホバー / フォーカス / タップのポップオーバーに逃がす
 * (数字の桁を動かさないよう、印は角に置く)。
 *
 * 表は `border-separate`。左端の列を `sticky` で固定するので、
 * `border-collapse` だと固定した列の枠線が描かれなくなる。
 */

/** 日付の列幅。31 列 + 固定列で 1400px 程度に収める。 */
const DAY_COL = 'w-9 min-w-9';
const LABEL_COL = 'w-64 min-w-64';
const TOTAL_COL = 'w-14 min-w-14';

/**
 * 横スクロール枠の見えている幅。
 *
 * マスを開いた訂正フォームは全列にまたがる `td` の中にあるので、
 * 幅を測って `sticky` で左端に留めないと、月末のマスを開いたときに
 * フォームが画面の外に出てしまう。
 */
function useVisibleWidth(ref: React.RefObject<HTMLDivElement | null>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setWidth(element.clientWidth));
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * 見出しのリンク。
 *
 * 見本 (`readOnly`) は LP に出るので、ログインの外に居る。飛び先が無いうえ
 * `EntityLink` は `useViewerRole` を呼ぶ (Provider の外では例外を投げる) ので、
 * ここでただの文字列に落とす。
 */
function RowLabel({
  target,
  readOnly,
  className,
  children,
}: {
  target: NavTarget;
  readOnly: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  if (readOnly) return <span className={className}>{children}</span>;
  return (
    <EntityLink target={target} className={className}>
      {children}
    </EntityLink>
  );
}

/** 外側の軸 (グループ見出し) が指すマスタ画面 */
function groupTarget(axis: GridAxis, group: { id: string; name: string }): NavTarget {
  return axis === 'member' ? toMember(group) : toProject(group);
}

/** 内側の軸 (行の見出し) が指すマスタ画面。外側と逆になる。 */
function rowTarget(axis: GridAxis, row: GridRow): NavTarget {
  return axis === 'member'
    ? toProject({ id: row.projectId, name: row.projectName })
    : toMember({ id: row.userId, name: row.userName });
}

export function TimeLogGrid({
  month,
  logs,
  projects,
  axis,
  canCreate,
  onChanged,
  readOnly = false,
}: {
  month: string;
  logs: TimeLog[];
  projects: Project[];
  axis: GridAxis;
  /** その行に記録を足せるか。行の持ち主で変わるので関数で受ける。 */
  canCreate: (userId: string) => boolean;
  onChanged: () => void;
  /**
   * 見本として置くとき (LP)。マスを開かず、見出しもリンクにしない。
   * 訂正フォームは `/api/time-logs` を叩くので、ログインの外では開けない。
   * メモのポップオーバーだけは残す (AI が書いたメモが付くことは実物の一部)。
   */
  readOnly?: boolean;
}) {
  const [selected, setSelected] = useState<{ rowKey: string; date: string } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const viewWidth = useVisibleWidth(wrapRef);

  const days = monthDays(month);
  const grid = buildGrid(logs, axis);
  const capitalizable = new Set(
    projects.filter((project) => project.isCapitalizable).map((project) => project.id),
  );
  const colCount = days.length + 2;

  return (
    <TableWrap ref={wrapRef} className="border-t border-line">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th
              scope="col"
              className={cn(
                LABEL_COL,
                'sticky top-0 left-0 z-30 border-r border-b border-line bg-surface-raised px-4 py-2 text-left font-mono text-[11px] font-normal tracking-wider text-ink-faint',
              )}
            >
              {axis === 'member' ? 'MEMBER / PROJECT' : 'PROJECT / MEMBER'}
            </th>
            {days.map((day) => (
              <th
                key={day.date}
                scope="col"
                className={cn(
                  DAY_COL,
                  'sticky top-0 z-20 border-r border-b border-line px-0 py-1.5 text-center font-mono text-[10px] leading-tight font-normal',
                  day.isWeekend ? 'bg-canvas text-ink-faint' : 'bg-surface-raised text-ink-subtle',
                )}
              >
                <span className="block">{day.weekdayLabel}</span>
                <span className="block text-[11px] text-ink-secondary">{day.day}</span>
              </th>
            ))}
            <th
              scope="col"
              className={cn(
                TOTAL_COL,
                'sticky top-0 right-0 z-30 border-b border-l border-line bg-surface-raised px-2 py-2 text-right font-mono text-[11px] font-normal tracking-wider text-ink-faint',
              )}
            >
              計
            </th>
          </tr>
        </thead>

        {grid.groups.map((group) => (
          <tbody key={group.id}>
            <tr>
              <th
                scope="row"
                className={cn(
                  LABEL_COL,
                  'sticky left-0 z-10 border-r border-b border-line bg-surface px-4 py-2 text-left text-sm font-bold',
                )}
              >
                <span className="flex items-center gap-2">
                  {/* 外側の軸はメンバーかプロジェクト。そのマスタ画面へ飛ばす
                      (mcp ロールはどちらも開けないので、リンクにはならない) */}
                  <RowLabel
                    target={groupTarget(axis, group)}
                    readOnly={readOnly}
                    className="truncate"
                  >
                    {group.name}
                  </RowLabel>
                  {axis === 'project' && capitalizable.has(group.id) ? (
                    <Badge variant="outline" className="shrink-0 whitespace-nowrap">
                      資産化対象
                    </Badge>
                  ) : null}
                </span>
              </th>
              {days.map((day) => {
                const value = group.dayHours[day.date] ?? 0;
                // プロジェクトの日計は複数人分の合計なので 24h を超えて当たり前。
                // 入力ミスとして意味を持つのは 1 人の日計のときだけ。
                const over = axis === 'member' && value > MAX_DAILY_HOURS;
                return (
                  <td
                    key={day.date}
                    className={cn(
                      'border-r border-b border-line px-0 py-2 text-center font-mono text-xs',
                      day.isWeekend ? 'bg-canvas' : 'bg-surface',
                      over ? 'font-bold text-alert' : 'text-ink-secondary',
                    )}
                    title={over ? `1 日 ${MAX_DAILY_HOURS} 時間を超えています` : undefined}
                  >
                    {value === 0 ? '' : hoursNum(value)}
                  </td>
                );
              })}
              <td
                className={cn(
                  TOTAL_COL,
                  'sticky right-0 z-10 border-b border-l border-line bg-surface px-2 py-2 text-right font-mono text-xs font-bold text-ink',
                )}
              >
                {hoursNum(group.total)}
              </td>
            </tr>

            {group.rows.map((row) => {
              const open = selected?.rowKey === row.key ? selected.date : null;
              return (
                <InnerRow
                  key={row.key}
                  row={row}
                  days={days}
                  target={rowTarget(axis, row)}
                  // 内側がプロジェクトのときだけ、行にバッジを付ける
                  isCapitalizable={axis === 'member' && capitalizable.has(row.projectId)}
                  canCreate={canCreate(row.userId)}
                  readOnly={readOnly}
                  openDate={open}
                  colCount={colCount}
                  viewWidth={viewWidth}
                  projects={projects}
                  onSelect={(date) =>
                    setSelected((current) =>
                      current?.rowKey === row.key && current.date === date
                        ? null
                        : { rowKey: row.key, date },
                    )
                  }
                  onClose={() => setSelected(null)}
                  onChanged={onChanged}
                />
              );
            })}
          </tbody>
        ))}

        <tfoot>
          <tr>
            <th
              scope="row"
              className={cn(
                LABEL_COL,
                'sticky left-0 z-10 border-r border-t border-line-strong bg-surface-raised px-4 py-2 text-left font-mono text-[11px] font-normal tracking-wider text-ink-faint',
              )}
            >
              日計
            </th>
            {days.map((day) => (
              <td
                key={day.date}
                className={cn(
                  'border-r border-t border-line-strong px-0 py-2 text-center font-mono text-xs text-ink-secondary',
                  day.isWeekend ? 'bg-canvas' : 'bg-surface-raised',
                )}
              >
                {grid.dayHours[day.date] ? hoursNum(grid.dayHours[day.date] ?? 0) : ''}
              </td>
            ))}
            <td
              className={cn(
                TOTAL_COL,
                'sticky right-0 z-10 border-t border-l border-line-strong bg-surface-raised px-2 py-2 text-right font-mono text-xs font-bold text-ink',
              )}
            >
              {hoursNum(grid.total)}
            </td>
          </tr>
        </tfoot>
      </table>
    </TableWrap>
  );
}

function InnerRow({
  row,
  days,
  target,
  isCapitalizable,
  canCreate,
  readOnly,
  openDate,
  colCount,
  viewWidth,
  projects,
  onSelect,
  onClose,
  onChanged,
}: {
  row: GridRow;
  days: GridDay[];
  /** 行の見出しから飛ぶ先 (内側の軸のマスタ画面) */
  target: NavTarget;
  isCapitalizable: boolean;
  canCreate: boolean;
  readOnly: boolean;
  openDate: string | null;
  colCount: number;
  viewWidth: number;
  projects: Project[];
  onSelect: (date: string) => void;
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <>
      <tr>
        <th
          scope="row"
          className={cn(
            LABEL_COL,
            'sticky left-0 z-10 border-r border-b border-line bg-surface-raised py-2 pr-3 pl-8 text-left text-sm font-normal',
          )}
        >
          {/* リンクにならない人 (mcp) でも見出しの色を保てるよう、
              muted は行に当てて中のリンクだけ色を持たせる */}
          <span className="flex items-center gap-2 text-ink-muted">
            <RowLabel target={target} readOnly={readOnly} className="truncate">
              {row.label}
            </RowLabel>
            {isCapitalizable ? (
              <Badge variant="outline" className="shrink-0 whitespace-nowrap">
                資産化対象
              </Badge>
            ) : null}
          </span>
        </th>

        {days.map((day) => {
          const cell = row.cells[day.date];
          const isOpen = openDate === day.date;
          // メモの無い記録もあるので、印を出すかどうかは本文の有無で決める
          const noted = cell?.logs.filter((log) => log.note) ?? [];
          // 記録も無く追加もできないマスは、開いても中身が無い。押せなくする。
          // 見本ではどのマスも開かないが、メモの付いたマスだけは押せるままに
          // して、ポップオーバーをキーボードからも開けるようにする。
          const openable = readOnly ? noted.length > 0 : cell !== undefined || canCreate;
          const button = (
            <button
              type="button"
              disabled={!openable}
              aria-expanded={readOnly ? undefined : isOpen}
              aria-label={`${row.userName} ${row.projectName} ${day.date} ${cell ? `${hoursNum(cell.hours)} 時間` : '記録なし'}${noted.length > 0 ? ' メモあり' : ''}`}
              onClick={readOnly ? undefined : () => onSelect(day.date)}
              className={cn(
                'relative block h-8 w-full text-center font-mono text-xs',
                openable && !readOnly ? 'hover:bg-brand-50' : 'cursor-default',
                cell ? 'text-ink' : 'text-ink-faint',
                isOpen && 'bg-brand-100 text-brand-900',
              )}
            >
              {cell ? hoursNum(cell.hours) : '·'}
              {noted.length > 0 ? (
                // 印は数字の桁を動かさない位置に置く (工数の列は縦に読むため)
                <span aria-hidden="true" className="absolute top-1 right-1 size-1 bg-brand-500" />
              ) : null}
            </button>
          );
          return (
            <td
              key={day.date}
              className={cn(
                'border-r border-b border-line p-0',
                day.isWeekend ? 'bg-canvas' : 'bg-surface-raised',
              )}
            >
              {noted.length > 0 ? (
                // 押すと訂正フォームが開いて全文の入力欄が出るので、
                // そのときポップオーバーは畳む (被せない)。
                // 見本は訂正フォームを開かないので、押したら開く側に倒す。
                <NotePopover content={<CellNotes logs={noted} />} dismissOnClick={!readOnly}>
                  {button}
                </NotePopover>
              ) : (
                button
              )}
            </td>
          );
        })}

        <td
          className={cn(
            TOTAL_COL,
            'sticky right-0 z-10 border-b border-l border-line bg-surface-raised px-2 py-2 text-right font-mono text-xs text-ink-secondary',
          )}
        >
          {hoursNum(row.total)}
        </td>
      </tr>

      {openDate ? (
        <tr>
          <td colSpan={colCount} className="p-0">
            <div
              className="sticky left-0"
              style={viewWidth > 0 ? { width: `${viewWidth}px` } : undefined}
            >
              <TimeLogEditor
                userId={row.userId}
                userName={row.userName}
                projectId={row.projectId}
                projectName={row.projectName}
                date={openDate}
                logs={row.cells[openDate]?.logs ?? []}
                projects={projects}
                canCreate={canCreate}
                onChanged={onChanged}
                onClose={onClose}
              />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
